import clsx from 'clsx';
import React, { useRef } from 'react';

import Button from '@components/Button';
import IconCheckmark from '@components/Images/IconCheckmark';
import { OfferOption, SelectedExtendedOffer } from '@utils/backend-api/subscribe';
import { YOUNG_PROFESSIONALS_PLAN } from '@utils/subscriptionCardOptions';

import Tag from './Tag';

interface OfferProps {
  isSingleOffer?: boolean;
  offer: OfferOption;
  selectedOfferCode?: string;
  setSelectedOffer?: (plan: SelectedExtendedOffer) => void;
}

const Offer = ({
  offer: {
    billingFrequency,
    billingFrequencyOverride,
    code,
    currentPrice,
    currentPriceDisplayOverride,
    description,
    isPro,
    isBestValue,
    listOfBenefits,
    originalPrice,
    originalPriceDisplayOverride,
    title,
  },
  selectedOfferCode,
  setSelectedOffer,
}: OfferProps) => {
  const offerRef = useRef<HTMLDivElement>(null);
  const isSelected = selectedOfferCode === code;
  const currentPriceToDisplay = currentPriceDisplayOverride || currentPrice;
  const hasDiscount = originalPrice !== currentPrice;
  const originalPriceToDisplay = originalPriceDisplayOverride || originalPrice;
  const isYPPlan = code.includes(YOUNG_PROFESSIONALS_PLAN);

  return (
    <div
      ref={offerRef}
      id={code}
      onClick={() =>
        setSelectedOffer({
          billingFrequency,
          code,
          currentPrice,
          description,
          isPro,
          isBestValue,
          offsetTop: offerRef.current?.offsetTop,
          originalPrice,
          title,
        })
      }
      className={clsx(
        'relative flex cursor-pointer flex-col overflow-hidden rounded-10 border border-solid border-transparent px-16 py-[44px] sm:h-full md:py-36',
        {
          'selected !border-primary-black': isSelected && !isPro,
          'selected !border-pro-red': isSelected && isPro,
          '!border-primary-black': isBestValue && !selectedOfferCode,
          'opacity-25 hover:opacity-50': !isSelected && selectedOfferCode,
        }
      )}
    >
      {isBestValue && (
        <Tag
          name="Best Value"
          className="best-value absolute left-0 right-0 top-0 !rounded-0 text-center"
        />
      )}
      <h2 className="m-0 min-h-[48px] text-center font-serif text-headline-xs font-bold text-black">
        {title}
      </h2>
      <div className="flex flex-col items-center justify-center gap-8">
        <h3 className="m-0 min-h-[20px] font-sans text-subtitle-serif text-grayscale-60 line-through">
          {hasDiscount && `$${originalPriceToDisplay.toFixed(2)}`}
        </h3>
        <h3 className="m-0 font-serif text-headline-xs font-bold text-black">
          ${currentPriceToDisplay.toFixed(2)}
          {billingFrequencyOverride && (
            <span className="ml-8 font-sans text-body-sm font-normal text-grayscale-60">
              {billingFrequencyOverride}
            </span>
          )}
        </h3>
        <span className="font-sans text-[13px] font-medium text-grayscale-60 sm:min-h-[20px]">
          {isYPPlan && '30 years or under'}
        </span>
      </div>
      <div className="my-16 border-0 border-t-2 border-solid border-grayscale-10 pt-16 md:my-24 md:pt-24">
        {listOfBenefits.length > 0 &&
          listOfBenefits.map((benefit, index) => (
            <p
              key={index}
              className={clsx(
                'mb-16 mt-0 flex items-start font-sans text-body-sm text-grayscale-60 last:mb-0',
                {
                  'font-bold': isPro,
                  'font-medium': !isPro,
                }
              )}
            >
              <IconCheckmark
                className={clsx('mr-8 mt-4 w-10 shrink-0', {
                  'text-primary-black': !isPro,
                  'text-pro-red': isPro,
                })}
              />
              <span>{benefit}</span>
            </p>
          ))}
      </div>
      <Button
        className={clsx('mt-auto w-full rounded-2 !capitalize', {
          '!border-pro-red': isPro,
          '!bg-white': isSelected,
          '!bg-pro-red': isPro && !isSelected,
          '!text-pro-red': isPro && isSelected,
          '!border-primary-black !text-primary-black': !isPro && isSelected,
        })}
      >
        {isSelected ? 'Selected' : 'Select'}
      </Button>
    </div>
  );
};

export default Offer;
